/**
 * Maps internal SKP edit/duplicate form state to Echo API write payloads.
 *
 * Internal pages (AddEditDuplicateInternalSKP) still use the legacy form model
 * (`promotion_type`, `start_date_campaign`, `campaign_cluster` as string, etc.)
 * and load detail via GET /skp/internal/v2/skp/{id} into SKPDetailsInterface.
 *
 * Echo update/copy endpoints expect a different shape (PostCreateSKPCopyParamInterface):
 *   - PUT  /skp/internal/v3/skps/{skp_id}
 *   - POST /commercial/internal/v2/skps/{skp_id}/copy
 *
 * This helper keeps the endpoint migration scoped to apiList + thunks without
 * refactoring the whole internal form, detail views, tables, and PDF templates.
 * Some fields are not simple renames: cluster name → campaign_cluster_id,
 * reimbursement from loaded detail, detail_from_skp_id from duplicate-products checkbox.
 *
 * Used in skpThunk submitSKP for `edit-internal-skp` and `duplicate-internal-skp`.
 */
import { PostCreateSKPCopyParamInterface } from 'features/Enterprise/SKP/@types/sourcingTypes'
import {
  type CampaignClusterType,
  type PostInternalSKPInterface,
  type SKPDetailsInterface,
} from 'features/Enterprise/SKP/utils/apiList'

export const mapInternalSkpToEchoCopyBody = (
  payload: PostInternalSKPInterface,
  skpDetails: SKPDetailsInterface,
  campaignClusterList: CampaignClusterType[],
  isDuplicateProduct: boolean,
): PostCreateSKPCopyParamInterface => {
  const clusterId =
    campaignClusterList.find((item) => item.cluster === payload.campaign_cluster)?.id ??
    campaignClusterList.find((item) => String(item.id) === payload.campaign_cluster)?.id ??
    0

  return {
    campaign_name: payload.campaign_name,
    promotion_type_id: payload.promotion_type,
    reimbursement_method_id: skpDetails.reimbursement_method || 'Transfer',
    campaign_start_date: payload.start_date_campaign,
    campaign_end_date: payload.end_date_campaign,
    campaign_cluster_id: clusterId,
    detail_from_skp_id: isDuplicateProduct ? skpDetails.id : null,
  }
}
